import * as Sentry from '@sentry/vue'
import PricingLineupApi from '@/api/pricingLineup'

// AHA-42054 — derives whether the Essential plan is absent from the lineup THIS
// viewer would see on the pricing page, by reading the shared
// GET /api/pricing/lineup. Single source of truth: the presenter never re-evaluates
// the Unleash flag itself, so it can never disagree with the pricing page about a
// given session/country. The `*_planName` essential→pro remap (plan.utils) keys off
// `isEssentialExcluded`.
//
// It ALSO exposes the per-plan/per-cycle Stripe price `lookupKey` the viewer sees
// on the pricing page (`priceLookupKeys`). The presenter upgrade flow forwards that
// lookupKey to the variant-aware v4 checkout so the price charged matches the price
// advertised on the pricing page (it would otherwise resolve the base price from a
// canonical `pro-yearly` key and diverge from a running price experiment).

const SET_LINEUP = 'SET_LINEUP'

const _state = {
  loaded: false,
  essentialExcluded: false,
  // { [plan]: { monthly?: lookupKey, yearly?: lookupKey } } — empty until loaded.
  priceLookupKeys: {},
  // { [plan]: { monthly?: { unitAmount, currency, currencySymbol }, yearly?: {...} } }
  // — the customer-facing amount the pricing page shows, when the lineup carries it.
  // Empty until loaded and whenever the backend does not (yet) return amounts, so
  // consumers must fall back to their own price source.
  priceAmounts: {},
}

// Reduce the lineup products to a { plan: { cycle: lookupKey } } map. Only products
// that actually carry a priced cycle contribute; free/enterprise (no prices) and any
// malformed entry are skipped, so a lookup simply misses and the caller falls back.
const buildPriceLookupKeys = (products) => {
  const map = {}
  if (!Array.isArray(products)) return map
  products.forEach((product) => {
    if (!product || !product.plan || !product.prices) return
    const cycles = {}
    Object.keys(product.prices).forEach((cycle) => {
      const lookupKey = product.prices[cycle] && product.prices[cycle].lookupKey
      if (lookupKey) cycles[cycle] = lookupKey
    })
    if (Object.keys(cycles).length) map[product.plan] = cycles
  })
  return map
}

// Reduce the lineup products to a { plan: { cycle: { unitAmount, currency,
// currencySymbol } } } map. Only cycles that actually carry a numeric `unitAmount`
// contribute, so when the backend has not (yet) added amounts the map stays empty
// and consumers transparently fall back to their own hardcoded price source.
const buildPriceAmounts = (products) => {
  const map = {}
  if (!Array.isArray(products)) return map
  products.forEach((product) => {
    if (!product || !product.plan || !product.prices) return
    const cycles = {}
    Object.keys(product.prices).forEach((cycle) => {
      const price = product.prices[cycle]
      if (price && Number.isFinite(Number(price.unitAmount))) {
        cycles[cycle] = {
          unitAmount: Number(price.unitAmount),
          currency: price.currency || null,
          currencySymbol: price.currencySymbol || null,
        }
      }
    })
    if (Object.keys(cycles).length) map[product.plan] = cycles
  })
  return map
}

const getters = {
  // True only once the lineup has loaded AND it has products none of which is the
  // essential tier. Defaults to false (Essential visible = current behaviour) while
  // loading and on any failure, so the funnel never hides a tier we're unsure about.
  isEssentialExcluded(state) {
    return state.essentialExcluded
  },
  isPricingLineupLoaded(state) {
    return state.loaded
  },
  // (plan, cycle) → the variant Stripe lookupKey the pricing page shows, or null.
  // Null means "no variant price known" → caller keeps its canonical behaviour.
  variantPriceLookupKey: (state) => (plan, cycle) => {
    if (!plan || !cycle) return null
    const byCycle = state.priceLookupKeys[plan]
    return (byCycle && byCycle[cycle]) || null
  },
  // (plan, cycle) → { unitAmount, currency, currencySymbol } the pricing page shows,
  // or null when the lineup carries no amount for that plan+cycle (→ caller uses its
  // own fallback price source).
  variantPrice: (state) => (plan, cycle) => {
    if (!plan || !cycle) return null
    const byCycle = state.priceAmounts[plan]
    return (byCycle && byCycle[cycle]) || null
  },
}

// Module-level coalescing: the lineup is viewer-global (not per-component), so
// concurrent callers during bootstrap share one in-flight request.
let inflight = null

const actions = {
  async fetchPricingLineup({ commit, state }) {
    if (state.loaded) return state.essentialExcluded
    if (inflight) return inflight

    inflight = (async () => {
      try {
        const products = await PricingLineupApi.getPricingLineupProducts()
        const essentialExcluded =
          Array.isArray(products)
          && products.length > 0
          && !products.some((product) => product && product.plan === 'essential')
        commit(SET_LINEUP, {
          essentialExcluded,
          priceLookupKeys: buildPriceLookupKeys(products),
          priceAmounts: buildPriceAmounts(products),
        })
        return essentialExcluded
      } catch (error) {
        // Safe default: keep Essential visible on any failure so we never break the
        // upgrade funnel because the (non-critical) lineup read failed.
        Sentry.captureException(error)
        commit(SET_LINEUP, { essentialExcluded: false, priceLookupKeys: {}, priceAmounts: {} })
        return false
      } finally {
        inflight = null
      }
    })()

    return inflight
  },
}

/* eslint-disable no-param-reassign */
const mutations = {
  [SET_LINEUP](state, { essentialExcluded, priceLookupKeys, priceAmounts }) {
    state.essentialExcluded = essentialExcluded
    state.priceLookupKeys = priceLookupKeys || {}
    state.priceAmounts = priceAmounts || {}
    state.loaded = true
  },
}
/* eslint-enable no-param-reassign */

export default {
  namespaced: true,
  state: _state,
  getters,
  actions,
  mutations,
}
